// `Notifications` settings section (US-186).
//
// In-app banner toggle + per-account deep links to the voice notification
// configuration. The toggle persists to `users.inappNotifications` via
// `PATCH /me`.

import { useNavigate } from '@tanstack/react-router';

import { useLinkedinAccounts, useMe, useUpdateProfile } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, Switch } from '@barel/ui';
import { Lightbulb } from 'lucide-react';

export function Notifications() {
  const t = useT('settings');
  const navigate = useNavigate();
  const me = useMe();
  const update = useUpdateProfile();
  const accounts = useLinkedinAccounts();
  const inApp =
    me.data?.user && typeof me.data.user.inappNotifications === 'boolean'
      ? me.data.user.inappNotifications
      : true;

  const handleChange = (value: boolean): void => {
    void update.mutateAsync({ inappNotifications: value });
  };

  return (
    <section
      data-section="notifications"
      aria-labelledby="settings-notifications-title"
      className="flex flex-col gap-6"
    >
      <header className="flex flex-col gap-1 border-b border-default pb-4">
        <h1 id="settings-notifications-title" className="text-lg font-semibold text-primary">
          {t('notifications.title')}
        </h1>
        <p className="text-xs text-tertiary">{t('notifications.subtitle')}</p>
      </header>

      <Banner
        tone="info"
        icon={<Lightbulb size={16} />}
        action={
          accounts.isLoading ? (
            <Button variant="secondary" size="sm" disabled>
              {t('notifications.loadingAccounts')}
            </Button>
          ) : accounts.data?.accounts.length ? (
            <div className="flex flex-wrap gap-2">
              {accounts.data.accounts.map((account) => (
                <Button
                  key={account.id}
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void navigate({
                      to: '/voice/$linkedinAccountId',
                      params: { linkedinAccountId: account.id },
                      hash: 'voice-notifications',
                    });
                  }}
                  data-testid={`settings-notifications-account-${account.id}`}
                >
                  {t('notifications.configureAccount', { accountName: account.name })}
                </Button>
              ))}
            </div>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void navigate({ to: '/accounts' });
              }}
              data-testid="settings-notifications-connect-account"
            >
              {t('notifications.connectAccount')}
            </Button>
          )
        }
      >
        {t('notifications.redirectHint')}
      </Banner>

      <div className="flex items-center justify-between gap-4 border-t border-subtle py-3">
        <div className="flex flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-primary">{t('notifications.inAppLabel')}</span>
          <span className="text-xs text-tertiary">{t('notifications.inAppHint')}</span>
        </div>
        <Switch
          checked={inApp}
          onCheckedChange={handleChange}
          aria-label={t('notifications.inAppLabel')}
          data-testid="settings-notifications-toggle"
        />
      </div>
    </section>
  );
}
